import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import classnames from "classnames";
import * as React from "react";
import { PurifiedHtml } from "../../purified-html";
import type { CalendarEventLabels } from "../types";
import styles from "./ExpandableDescription.module.css";

export interface ExpandableDescriptionProps {
  sanitizedDescription: string;
  labels: Pick<
    CalendarEventLabels,
    "descriptionLabel" | "showMore" | "showLess"
  >;
}

export const ExpandableDescription = ({
  sanitizedDescription,
  labels,
}: ExpandableDescriptionProps) => {
  const hasDescription = Boolean(
    sanitizedDescription && sanitizedDescription.trim(),
  );
  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    React.useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    React.useState(false);
  const descriptionRef = React.useRef<HTMLDivElement>(null);

  // Check for overflow on mount and resize
  React.useLayoutEffect(() => {
    const checkOverflow = () => {
      if (descriptionRef.current && !isDescriptionExpanded) {
        const isOverflowing =
          descriptionRef.current.scrollHeight >
          descriptionRef.current.clientHeight;
        setIsDescriptionOverflowing(isOverflowing);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (descriptionRef.current) {
      resizeObserver.observe(descriptionRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [isDescriptionExpanded, sanitizedDescription]);

  const handleDescriptionToggle = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();

      // Don't toggle if user is selecting text
      if (window && window.getSelection()?.toString()) {
        return;
      }

      setIsDescriptionExpanded((prev) => !prev);
    },
    [],
  );

  const showExpandButton =
    hasDescription && (isDescriptionOverflowing || isDescriptionExpanded);

  if (!hasDescription) {
    return null;
  }

  return (
    <>
      <Box>
        <Text color="subtle" size="medium">
          {labels.descriptionLabel}
        </Text>
        <div
          ref={descriptionRef}
          className={classnames(styles.description, {
            [styles.expandableDescription]:
              showExpandButton && !isDescriptionExpanded,
            [styles.descriptionCollapsed]: !isDescriptionExpanded,
            [styles.truncated]:
              !isDescriptionExpanded && isDescriptionOverflowing,
          })}
          onClick={showExpandButton ? handleDescriptionToggle : undefined}
        >
          {showExpandButton && !isDescriptionExpanded && (
            <Button
              className={styles.showMoreButton}
              onClick={handleDescriptionToggle}
              variant="transparent"
              size="medium"
              hasNoUnderline={false}
              aria-label={labels.showMore}
            >
              {labels.showMore}
            </Button>
          )}
          <PurifiedHtml
            html={sanitizedDescription}
            className={styles.descriptionHtml}
          />
        </div>
      </Box>

      {showExpandButton && isDescriptionExpanded && (
        <Box>
          <Button
            className={styles.showLessButton}
            onClick={handleDescriptionToggle}
            variant="transparent"
            size="medium"
            hasNoUnderline={false}
            aria-label={labels.showLess}
          >
            {labels.showLess}
          </Button>
        </Box>
      )}

      {showExpandButton && !isDescriptionExpanded && (
        <Box>
          <Button
            className={styles.safariShowMoreButton}
            onClick={handleDescriptionToggle}
            variant="transparent"
            size="medium"
            hasNoUnderline={false}
            aria-label={labels.showMore}
          >
            {labels.showMore}
          </Button>
        </Box>
      )}
    </>
  );
};
